/**
 * @fileoverview MCTS Analytics UI Component - Decision Tree Focus
 * 
 * RESPONSIBILITIES:
 * - Display MCTS analysis data (decision trees, move evaluations)
 * - Show preprocessing logs and move filtering
 * - Display top moves considered with statistics
 * - Export analysis data for post-game review
 */

import React, { useContext, useEffect, useState } from "react";
import { GameContext } from "../../../../components/Providers/GameContext";
import { Mode, State, MoveType } from "../../types";
import { MCTSAnalysisUIData } from "./MCTSAnalysisUIData";

interface MCTSAnalyticsProps {
  mctsAnalysisData?: MCTSAnalysisUIData | null;
  cumulativeLog?: string;
}

interface DisplayableAnalytics {
  turnNumber: number;
  playerToMove: 'Light' | 'Dark';
  mctsAnalysis: MCTSAnalysisUIData | null;
  isGameOver: boolean;
  gameWinner?: 'Light' | 'Dark' | 'Draw';
  lastAnalysisTimeMs?: number;
  rationaleSummary: string;  // New: Per-turn summary
}

const MCTSAnalytics: React.FC<MCTSAnalyticsProps> = ({ mctsAnalysisData, cumulativeLog }) => {
  const { gameState } = useContext(GameContext)!;
  const [currentDisplayData, setCurrentDisplayData] = useState<DisplayableAnalytics | null>(null);
  const [historicalData, setHistoricalData] = useState<DisplayableAnalytics[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);

  // Dark mode detection - same pattern as AIStats
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const checkDark = () => {
      let el = document.querySelector('.container');
      if (el && el.classList.contains('dark')) setIsDark(true);
      else setIsDark(false);
    };
    checkDark();
    const observer = new MutationObserver(checkDark);
    const container = document.querySelector('.container');
    if (container) observer.observe(container, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const [lastProcessedTurn, setLastProcessedTurn] = useState<number | null>(null);

  useEffect(() => {
    if (gameState.mode === Mode.AI || gameState.mode === Mode.TestAI || gameState.mode === Mode.Watch) {
      if (!gameState.isLightTurn && gameState.turnCount !== lastProcessedTurn) {
        // Reset only on new Dark turn
        setHistoricalData([]);
        setCurrentDisplayData(null);

        const isGameOver = gameState.state === State.PostGame || gameState.lightWon !== undefined;
        
        const analysisData: MCTSAnalysisUIData | null = mctsAnalysisData || null;

        const rationaleSummary = generateRationaleSummary(analysisData);

        const displayData: DisplayableAnalytics = {
          turnNumber: gameState.turnCount,
          playerToMove: 'Dark',
          mctsAnalysis: analysisData,
          isGameOver: isGameOver,
          gameWinner: isGameOver ? (gameState.lightWon === true ? 'Light' : gameState.lightWon === false ? 'Dark' : undefined) : undefined,
          lastAnalysisTimeMs: analysisData?.executionTime !== undefined ? Math.round(analysisData.executionTime) : undefined,
          rationaleSummary,
        };

        setCurrentDisplayData(displayData);

        if (analysisData) { 
          setHistoricalData([displayData]);
        }

        setLastProcessedTurn(gameState.turnCount);
      }
      // On Light turns, do nothing - persist existing data
    }
  }, [mctsAnalysisData, gameState.turnCount, gameState.isLightTurn, gameState.state, gameState.lightWon, gameState.mode, lastProcessedTurn]);
  
  const exportAnalysisData = () => {
    const exportObject = {
      gameEnded: currentDisplayData?.isGameOver,
      winner: currentDisplayData?.gameWinner,
      finalTurn: currentDisplayData?.turnNumber,
      fullLog: historicalData,
      cumulativeStringLog: cumulativeLog
    };
    const dataStr = JSON.stringify(exportObject, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `zodiac_mcts_analysis_${new Date().toISOString().slice(0,10)}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // New: Generate concise rationale summary per turn
  const generateRationaleSummary = (analysis: MCTSAnalysisUIData | null): string => {
    if (!analysis) return "No analysis available";
    const priority = analysis.preprocessingLog?.find(log => log.includes("Priority")) || "MCTS fallback";
    const threats = analysis.preprocessingLog?.filter(log => log.includes("threat"))?.join(", ") || "No threats";
    return `Decision: ${analysis.reasonForMove || "N/A"} | Priority: ${priority} | Threats: ${threats} | Eval: ${(analysis.topMovesConsidered?.[0]?.heuristicEval || 0).toFixed(1)}`;
  };

  const renderMctsMove = (moveData: any, index?: number, context?: 'top' | 'final') => {
    // ... Existing renderMctsMove ...
  };

  if (!currentDisplayData || !currentDisplayData.mctsAnalysis || (gameState.mode !== Mode.AI && gameState.mode !== Mode.TestAI && gameState.mode !== Mode.Watch)) {
    // ... Existing ...
  }

  const analysis = currentDisplayData?.mctsAnalysis;
  const showDetailedAnalysis = currentDisplayData && currentDisplayData.mctsAnalysis && (gameState.mode === Mode.AI || gameState.mode === Mode.TestAI || gameState.mode === Mode.Watch);

  return (
    <div style={{ 
      maxWidth: '100%',
      overflow: 'hidden',
      wordWrap: 'break-word'
    }}>
      {isExpanded && showDetailedAnalysis && (
        <div>

          {/* NEW: Game Rationale Log Section - Cumulative across turns */}
          <div style={{ marginBottom: '16px' }}>
            <h4 style={{ color: isDark ? '#f1f1f1' : '#495057', marginBottom: '8px' }}>📜 Game Rationale Log</h4>
            <div style={{ 
              backgroundColor: isDark ? '#1a1d22' : '#f8f9fa', 
              border: isDark ? '1px solid #404040' : '1px solid #dee2e6', 
              borderRadius: '4px',
              padding: '8px',
              maxHeight: '600px',
              maxWidth: '400px',
              overflowY: 'auto',
              overflowX: 'hidden',
              wordWrap: 'break-word',
              width: '100%'
            }}>
              {historicalData.map((data, index) => (
                <div key={index} style={{ 
                  fontSize: '12px', 
                  marginBottom: '8px',
                  padding: '6px',
                  borderBottom: '1px solid #dee2e6',
                  color: data.isGameOver ? '#dc3545' : (isDark ? '#f1f1f1' : '#495057'),
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-all',
                  hyphens: 'auto',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}>
                  Turn {data.turnNumber} ({data.playerToMove}): {data.rationaleSummary}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCTSAnalytics;